import L, { LatLngBoundsExpression } from "leaflet";
import { renderToStaticMarkup, renderToString } from "react-dom/server";
import { hexToCssHsl } from "../../../../common/utils";
import { MeasurableValueDTO } from "../../../../datasources/api-client/data-contracts";
import { default as countriesGeojson } from "./countriesGeojson.json";
import "./style.css";
import { LocationIcon } from "../../../../assets/icons";
import { getNodeTypeIcon } from "../../../../constants/nodeTypeIcons";

/**
 * Represents a point on the map
 */
export interface Point {
	lat: number;
	lng: number;
}
/**
 * Represents a range of values, used in country heatmaps
 */
export interface Range {
	min: number;
	max: number;
}

/**
 * Represents data that can be used to create a country heatmap
 */
export interface HeatData {
	iso3: string;
	heat: MeasurableValueDTO;
}

export interface Rect {
	bounds: [number, number][];
	latDelta: number;
	lngDelta: number;
}

/**
 * creates an icon used for marker clusters
 * @param cluster
 * @param color
 * @param nodesNumber
 * @returns
 */
export const groupMarkerIcon = (count: number, color: string, nodesNumber: number) => {
	// nodes number contained in the cluster
	const hlsColor = hexToCssHsl(color);

	const markerLuminosity = 60 - (count * 50) / nodesNumber;

	// color of the inner part of the marker
	let markerColor = `hsl(${hlsColor.h}, ${hlsColor.s}%, ${Math.round(markerLuminosity)}%)`;
	// color of the outer part of the marker
	let baseColor = `hsla(${hlsColor.h}, ${hlsColor.s}%, ${Math.round(markerLuminosity)}%, 0.45)`;

	const size = 25 + Math.min(count, 100) * 0.225;
	const pulseSize = size + 18;

	return L.divIcon({
		html: `
            <div style="position: relative; width: ${pulseSize}px; height: ${pulseSize}px;">
                <div style="
                        background: ${baseColor};
                        width: ${pulseSize}px;
                        height: ${pulseSize}px;
                        border-radius: 50%;
                        position: absolute;
                        top: 0; 
						left: 0;
                        animation: pulse 3s infinite linear;
                        z-index: 1;
                    ">
                </div>
                <div style="
                    background: ${markerColor};
                    width: ${size}px;
                    height: ${size}px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-weight: bold;
                    position: absolute;
					top: 50%; 
					left: 50%;
					transform: translate(-50%, -50%);
                    z-index: 2;
                ">
                ${count}
                </div>
            </div>
            `,
		className: "",
		iconSize: L.point(pulseSize, pulseSize, true),
	});
};

/**
 * Creates a default scale based on the given heatData
 * @param heatData
 * @param steps
 * @returns
 */
export const defaultScale = (heatData: HeatData[], steps: number = 5): Range[] => {
	// get only heat values from given heatData
	const heatValues = heatData.map((hdata) => hdata.heat.value);
	// calculate min, max and step value
	const minVal = Math.min.apply(null, heatValues);
	const maxVal = Math.max.apply(null, heatValues);
	const stepVal = (maxVal - minVal) / steps;

	// create the scale
	const scale: Range[] = [];
	for (var i = 0; i < steps; i++) {
		// add the step
		scale.push({ min: minVal + stepVal * i, max: minVal + stepVal * (i + 1) });
	}

	return scale;
};

/**
 * Retrieves country info from the countriesGeojson.json file
 * If the given string is not associated with any country, returns undefined
 * @param iso3
 * @returns
 */
export const getCountry = (iso3: string) => {
	return countriesGeojson.countries.find((countryData) => countryData.properties.iso3 === iso3);
};

/**
 * calculates and returns the color to which the given value
 * is associated using the given scale
 *
 * a baseColor is required (in hex)
 *
 * if the value is out of the scale boundaries, the returned color
 * is the closest to the given value
 * @param value
 * @param scale
 * @param baseColor
 * @param minLuminosity
 * @param maxLuminosity
 * @returns @string color
 */
export const getHeatColor = (
	value: number | undefined,
	scale: Range[],
	baseColor: string,
	minLuminosity: number = 35,
	maxLuminosity: number = 85
) => {
	if (value === undefined) return "#00000000"; // if undefined value, return transparent
	// converting basecolor from hex to hls for calculations
	const hlsBaseColor = hexToCssHsl(baseColor);
	const luminosityStep = (maxLuminosity - minLuminosity) / scale.length;

	// for each step of the scale
	for (var i = 0; i < scale.length; i++) {
		if (scale[i].min > value) {
			const luminosity = i > 0 ? maxLuminosity - luminosityStep * (i - 1) : maxLuminosity - luminosityStep * i;
			return `hsl(${hlsBaseColor.h}, ${hlsBaseColor.s}%, ${Math.round(luminosity)}%)`;
		}
	}
	// If the value was higher than any step, return the last step's color
	return `hsl(${hlsBaseColor.h}, ${hlsBaseColor.s}%, ${Math.round(minLuminosity)}%)`;
};

export const calcMapConfig = (points?: Point[]) => {
	// if no points are given, returns full size bounds
	if (!points || points.length === 0)
		return {
			center: {
				lat: 53,
				lng: 9,
			},
			zoom: 3,
		};

	const pointsRect = calcPointsRect(points);

	let latZoom;
	let lngZoom;
	let tempLatRange = 180;
	let tempLngRange = 360;
	for (let i = 1; i <= 20; i++) {
		if (tempLatRange > pointsRect.latDelta && pointsRect.latDelta > tempLatRange / 2) latZoom = i;
		if (tempLngRange > pointsRect.lngDelta && pointsRect.lngDelta > tempLngRange / 2) lngZoom = i;
		if (latZoom && lngZoom) break;
		tempLatRange /= 2;
		tempLngRange /= 2;
	}
	const zoom = Math.min(latZoom || 1, lngZoom || 1);

	const center = calcPointsCenter(points);

	const mapRect = extendRect(pointsRect, 40 / 100); // extends rect by 40%

	return {
		bounds: mapRect.bounds as LatLngBoundsExpression,
		center: center,
		zoom: zoom,
	};
};

const extendRect = (rect: Rect, extension: number) => {
	const latExtension = rect.latDelta * (extension / 2);
	const lngExtension = rect.lngDelta * (extension / 2);

	const minLat = rect.bounds[0][0] - latExtension;
	const minLng = rect.bounds[0][1] - lngExtension;
	const maxLat = rect.bounds[1][0] + latExtension;
	const maxLng = rect.bounds[1][1] + lngExtension;

	const extended: Rect = {
		bounds: [
			[minLat, minLng],
			[maxLat, maxLng],
		],
		latDelta: Math.abs(maxLat - minLat),
		lngDelta: Math.abs(maxLng - minLng),
	};

	return extended;
};

/**
 * Calculates the rect of the given points
 * @param points
 * @returns
 */
const calcPointsRect = (points: Point[]): Rect => {
	// if no points are given, returns full size bounds
	if (!points || points.length === 0)
		return {
			bounds: [
				[-90, -180],
				[90, 180],
			],
			latDelta: 180,
			lngDelta: 360,
		};
	// arrays of latitudes and longitudes
	const lats = points.map((point) => point.lat);
	const lngs = points.map((point) => point.lng);

	const minLat = Math.min.apply(null, lats);
	const minLng = Math.min.apply(null, lngs);
	const maxLat = Math.max.apply(null, lats);
	const maxLng = Math.max.apply(null, lngs);

	const rect: Rect = {
		bounds: [
			[minLat, minLng],
			[maxLat, maxLng],
		],
		latDelta: Math.abs(maxLat - minLat),
		lngDelta: Math.abs(maxLng - minLng),
	};

	return rect;
};

/**
 * Calculates the coordinates of the center of the given points
 * @param points
 * @returns
 */
export const calcPointsCenter = (points?: Point[]) => {
	// if no points are given, returns center of western europe
	if (!points || points.length === 0) return { lat: 53, lng: 9 };

	// arrays of latitudes and longitudes
	const lats = points.map((point) => point.lat);
	const lngs = points.map((point) => point.lng);

	// calculate center coords
	const centerLat = (Math.min.apply(null, lats) + Math.max.apply(null, lats)) / 2;
	const centerLng = (Math.min.apply(null, lngs) + Math.max.apply(null, lngs)) / 2;

	return {
		lat: centerLat,
		lng: centerLng,
	};
};

/**
 * Calculates and returns the center of the given polygon
 * @param polygon
 * @returns
 */
export const calcPolygonCenter = (polygon: Point[][]) => {
	// if no polygon is given, returns center of western europe
	if (!polygon || polygon.length === 0) return { lat: 53, lng: 9 };

	// points is the concatenation of every line of the polygon
	var points: Point[] = [];
	polygon.map((line) => {
		points = points.concat(line);
		return 0;
	});

	// calculates and returns the center of the polygon's points
	return calcPointsCenter(points);
};

/**
 * creates an icon for a single marker
 * @param primaryColor
 * @returns
 */
export const markerIcon = (primaryColor: string, nodeType?: number | null) => {
	const IconComponent = getNodeTypeIcon(nodeType) || LocationIcon;

	const iconHtml = renderToStaticMarkup(
		<div
			style={{
				width: 30,
				height: 30,
				borderRadius: "50%",
				backgroundColor: "white",
				border: `1px solid ${primaryColor}`,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				boxSizing: "border-box",
			}}
		>
			<IconComponent
				style={{
					width: 22,
					height: 22,
					fill: primaryColor,
				}}
			/>
		</div>
	);

	return L.divIcon({
		html: iconHtml,
		className: "",
		iconSize: [32, 32],
		iconAnchor: [16, 16],
		popupAnchor: [0, -16],
	});
};

/**
 * returns an icon that displays a label
 * @param title label title - will be placed above the text and converted to uppercase but with a smaller font-size
 * @param text label text - will be placed under the title but with a larger font-size and bold
 * @param color title and text color
 * @returns
 */
export const labelIcon = (title: string = "", text: string = "", color: string = "#000") => {
	const iconHtml = `
        <div style="
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: ${color};            
        ">
            <p style="
				font-size: 9px; 
				margin:0; 
				line-height: 10px; 
				text-shadow: #fff 0px 0 2px;
			">${title.toUpperCase()}</p>
            <p style="
				font-weight: bold; 
				font-size: 10px; 
				margin:0; 
				line-height: 10px;
				text-shadow: #fff 0px 0 2px;
			">${text}</p>
        </div>
    `;
	return L.divIcon({
		html: iconHtml,
		className: "",
	});
};

export const tripNodeDivIcon = (nodeType: number, color: string, size: number) => {
	const iconHtml = renderToString(tripNodeIcon(nodeType, color, size));
	return L.divIcon({
		html: iconHtml,
		className: "",
		iconSize: [size, size],
		iconAnchor: [size / 2, size * (7 / 8)],
	});
};

export const tripNodeIcon = (nodeType: number, color: string, size?: number) => {
	const style = size
		? { fill: color, stroke: "rgba(0,0,0,0.5)", strokeWidth: 0.5, width: size, height: size }
		: { fill: color, stroke: "rgba(0,0,0,0.5)", strokeWidth: 0.5 };
	const IconComponent = getNodeTypeIcon(nodeType);
	if (!IconComponent) return null;
	return <IconComponent style={style} sx={{ color: color, fontSize: "small" }} />;
};
